import { useCallback, useEffect, useState } from 'react';
import { ActionIcon, Badge, Box, Drawer, Group, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Mail, X } from 'react-feather';
import {
  attendanceFollowUpsApi,
  type AbsenceEmailTemplate,
} from '../../../services/api/attendanceFollowUps';
import { FollowUpParticipantList } from './attendanceFollowUps/FollowUpParticipantList';
import { FollowUpParticipantDetail } from './attendanceFollowUps/FollowUpParticipantDetail';
import classes from './AttendanceFollowUpsPane.module.css';

interface AttendanceFollowUpsPaneProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  initialSessionId?: string;
  initialParticipantId?: string;
  // MED-778: Feature toggle for hiding send buttons
  participantFeaturesEnabled?: boolean;
}

/**
 * MED-738 two-level drill-down pane.
 *
 * Level 1: participant-centric list with session filter, group-by toggle,
 * and batch send bar. Click a row to drill down.
 *
 * Level 2: single participant's accordion of pending + past follow-ups with
 * in-place edit, skip-with-note modal, and template override.
 *
 * `initialParticipantId` opens Level 2 directly (deep-link support — AC46).
 */
export function AttendanceFollowUpsPane({
  opened,
  onClose,
  cohortId,
  initialSessionId,
  initialParticipantId,
  participantFeaturesEnabled = true,
}: AttendanceFollowUpsPaneProps) {
  const [activeParticipantId, setActiveParticipantId] = useState<string | null>(null);
  const [templates, setTemplates] = useState<AbsenceEmailTemplate[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [listRefreshNonce, setListRefreshNonce] = useState(0);

  // Reset internal state when the pane opens/closes.
  useEffect(() => {
    if (opened) {
      setActiveParticipantId(initialParticipantId ?? null);
    } else {
      setActiveParticipantId(null);
    }
  }, [opened, initialParticipantId]);

  // Load templates once per mount (small, static-ish list).
  useEffect(() => {
    if (!opened || templates.length > 0) return;
    attendanceFollowUpsApi
      .listTemplates()
      .then(setTemplates)
      .catch(() => {
        notifications.show({
          title: 'Error',
          message: 'Failed to load email templates',
          color: 'red',
        });
      });
  }, [opened, templates.length]);

  // Keep the header pending pill in sync with the L1 fetch result.
  useEffect(() => {
    if (!opened) return;
    let cancelled = false;
    attendanceFollowUpsApi
      .listParticipants(cohortId)
      .then(res => {
        if (!cancelled) setPendingCount(res.total_pending);
      })
      .catch(() => {
        /* silent — L1 view will surface its own error */
      });
    return () => {
      cancelled = true;
    };
  }, [opened, cohortId, listRefreshNonce]);

  const handleBackToList = useCallback(() => {
    setActiveParticipantId(null);
    // Bump refresh nonce so the L1 list re-fetches (skip/send/override on L2
    // may have changed counts).
    setListRefreshNonce(n => n + 1);
  }, []);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="80%"
      padding={0}
      withCloseButton={false}
      zIndex={301}
      styles={{
        body: { height: '100%', display: 'flex', flexDirection: 'column' },
        content: { display: 'flex', flexDirection: 'column' },
      }}
    >
      <Box className={classes.paneContainer}>
        <Box className={classes.header}>
          <Group gap="sm" className={classes.headerTitle}>
            <Mail size={20} />
            <Text fw={700} size="lg">
              Attendance Follow-Ups
            </Text>
            {pendingCount > 0 && (
              <Badge
                size="sm"
                variant="filled"
                styles={{
                  root: {
                    background: 'var(--medley-flag-bg)',
                    color: 'var(--medley-flag-text)',
                  },
                }}
              >
                {pendingCount} PENDING
              </Badge>
            )}
          </Group>
          <ActionIcon variant="subtle" onClick={onClose} aria-label="Close">
            <X size={20} />
          </ActionIcon>
        </Box>

        <Box className={classes.content} style={{ overflowY: 'auto', flex: 1 }}>
          {activeParticipantId ? (
            <FollowUpParticipantDetail
              cohortId={cohortId}
              cohortParticipantId={activeParticipantId}
              onBack={handleBackToList}
              templates={templates}
            />
          ) : (
            <FollowUpParticipantList
              cohortId={cohortId}
              initialSessionId={initialSessionId ?? null}
              onSelectParticipant={setActiveParticipantId}
              refreshNonce={listRefreshNonce}
              participantFeaturesEnabled={participantFeaturesEnabled}
            />
          )}
        </Box>
      </Box>
    </Drawer>
  );
}

export default AttendanceFollowUpsPane;
